/**
 * Detail Modal Component
 * Displays full error details for failed operations
 */

import { useEffect } from 'react';
import type { TenderLinkDetail, AdminParcelDetail } from '@tender-monitor/shared/types/admin';
import { formatAbsoluteTime } from '../../utils/formatTime';

type DetailModalProps = {
  type: 'tender-link' | 'parcel';
  data: TenderLinkDetail | AdminParcelDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
};

function DetailRow({ label, value, isError = false }: { label: string; value: React.ReactNode; isError?: boolean }) {
  return (
    <div className="detail-row">
      <div className="detail-row__label">{label}</div>
      <div className={`detail-row__value ${isError ? 'detail-row__value--error' : ''}`}>
        {value || '-'}
      </div>
    </div>
  );
}

function formatExtractionSource(source: string | null | undefined): string {
  if (!source) return '-';
  switch (source) {
    case 'html': return 'Strona HTML';
    case 'pdf': return 'Załącznik PDF';
    case 'docx': return 'Załącznik DOCX';
    default: return source;
  }
}

function formatTenderType(type: string | null | undefined): string {
  if (!type) return '-';
  switch (type) {
    case 'sale': return 'Sprzedaż';
    case 'lease': return 'Dzierżawa';
    case 'auction': return 'Licytacja';
    case 'other': return 'Inne';
    default: return type;
  }
}

function formatPrice(price: number | null | undefined): string {
  if (price == null) return '-';
  return price.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
}

function formatArea(area: number | null | undefined): string {
  if (area == null) return '-';
  return `${area.toLocaleString('pl-PL')} m²`;
}

function formatConfidence(confidence: number | null | undefined): React.ReactNode {
  if (confidence == null) return '-';
  const percent = Math.round(confidence * 100);
  const color = percent >= 80 ? '#276749' : percent >= 50 ? '#744210' : '#c53030';
  return <span style={{ color, fontWeight: 500 }}>{percent}%</span>;
}

function formatGeolocationStatus(status: string): React.ReactNode {
  const statusMap: Record<string, { label: string; color: string }> = {
    pending: { label: 'Oczekuje', color: '#718096' },
    success: { label: 'Sukces', color: '#276749' },
    failed: { label: 'Błąd', color: '#c53030' },
    not_found: { label: 'Nie znaleziono', color: '#744210' },
  };
  const info = statusMap[status] || { label: status, color: '#718096' };
  return <span style={{ color: info.color, fontWeight: 500 }}>{info.label}</span>;
}

function TenderLinkDetails({ data }: { data: TenderLinkDetail }) {
  return (
    <>
      {/* Basic info section */}
      <div className="detail-section">
        <h4 className="detail-section__title">Informacje podstawowe</h4>
        <DetailRow label="ID" value={data.id} />
        <DetailRow label="Status" value={<span className={`status-badge status-badge--${data.status}`}>{data.status}</span>} />
        <DetailRow label="Źródło" value={data.sourceName} />
        <DetailRow label="Tytuł" value={data.title} />
        <DetailRow
          label="URL"
          value={
            <a href={data.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
              {data.url}
            </a>
          }
        />
        <DetailRow label="Odkryto" value={formatAbsoluteTime(data.discoveredAt)} />
        <DetailRow label="Przetworzone" value={data.processedAt ? formatAbsoluteTime(data.processedAt) : 'Nie przetworzone'} />
        {data.errorMessage && (
          <DetailRow label="Błąd" value={data.errorMessage} isError />
        )}
      </div>

      {/* Extracted tender data section */}
      {data.tender && (
        <div className="detail-section">
          <h4 className="detail-section__title">Wyekstrahowane dane przetargu</h4>
          <DetailRow label="ID Przetargu" value={data.tenderId} />
          <DetailRow label="Typ" value={formatTenderType(data.tender.tenderType)} />
          <DetailRow label="Cena" value={formatPrice(data.tender.price)} />
          <DetailRow label="Data przetargu" value={data.tender.tenderDate || '-'} />
          <DetailRow label="Pewność ekstrakcji" value={formatConfidence(data.tender.confidence)} />
          <DetailRow
            label="Źródło danych"
            value={
              <span style={{
                color: data.extractionSource === 'html' ? '#718096' : '#276749',
                fontWeight: data.extractionSource !== 'html' ? 500 : 400
              }}>
                {formatExtractionSource(data.extractionSource)}
              </span>
            }
          />
          {data.tender.extractionNotes && (
            <DetailRow label="Notatki ekstrakcji" value={data.tender.extractionNotes} />
          )}
          {data.tender.attachments && data.tender.attachments.length > 0 && (
            <DetailRow
              label="Załączniki"
              value={
                <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                  {data.tender.attachments.map((att, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem' }}>
                      <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                        {att.title || att.url}
                      </a>
                      {att.type && <span style={{ color: '#718096', marginLeft: '0.5rem' }}>({att.type})</span>}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </div>
      )}

      {/* Parcels section */}
      {data.parcels && data.parcels.length > 0 && (
        <div className="detail-section">
          <h4 className="detail-section__title">Działki ({data.parcels.length})</h4>
          {data.parcels.map((parcel, idx) => (
            <div key={parcel.id} className="parcel-card">
              <div className="parcel-card__header">
                <strong>Działka {idx + 1}: {parcel.parcelNumber}</strong>
                {formatGeolocationStatus(parcel.geolocationStatus)}
              </div>
              <div className="parcel-card__body">
                <DetailRow label="Obręb" value={parcel.district} />
                <DetailRow label="Gmina" value={parcel.commune} />
                <DetailRow label="TERYT ID" value={parcel.terytId} />
                <DetailRow label="Powierzchnia" value={formatArea(parcel.areaM2)} />
                {parcel.geolocationError && (
                  <DetailRow label="Błąd geolokalizacji" value={parcel.geolocationError} isError />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No data extracted message */}
      {!data.tender && data.status === 'completed' && (
        <div className="detail-section">
          <p style={{ color: '#718096', fontStyle: 'italic' }}>
            Przetwarzanie zakończone, ale nie wyekstrahowano danych przetargu.
          </p>
        </div>
      )}
    </>
  );
}

function ParcelDetails({ data }: { data: AdminParcelDetail }) {
  return (
    <>
      <DetailRow label="ID" value={data.id} />
      <DetailRow
        label="Status"
        value={<span className={`status-badge status-badge--${data.geolocationStatus}`}>{data.geolocationStatus}</span>}
      />
      <DetailRow label="Źródło" value={data.sourceName} />
      <DetailRow label="Numer działki" value={data.parcelNumber} />
      <DetailRow label="Obręb" value={data.district} />
      <DetailRow label="Gmina" value={data.commune} />
      <DetailRow label="TERYT ID" value={data.terytId} />
      <DetailRow label="Powierzchnia" value={data.areaM2 ? `${data.areaM2.toLocaleString('pl-PL')} m²` : null} />
      <DetailRow label="Utworzono" value={formatAbsoluteTime(data.createdAt)} />
      <DetailRow label="Zaktualizowano" value={formatAbsoluteTime(data.updatedAt)} />
      <DetailRow label="ID Przetargu" value={data.tenderId} />
      {data.geolocationError && (
        <DetailRow label="Błąd geolokalizacji" value={data.geolocationError} isError />
      )}
    </>
  );
}

export function DetailModal({ type, data, loading, error, onClose }: DetailModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const title = type === 'tender-link' ? 'Szczegóły linku' : 'Szczegóły działki';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Zamknij">
            ×
          </button>
        </div>
        <div className="modal-body">
          {loading && <div className="loading-state">Wczytywanie szczegółów...</div>}

          {error && (
            <div className="error-state">
              <p className="error-state__message">Błąd: {error}</p>
            </div>
          )}

          {!loading && !error && data && type === 'tender-link' && (
            <TenderLinkDetails data={data as TenderLinkDetail} />
          )}

          {!loading && !error && data && type === 'parcel' && (
            <ParcelDetails data={data as AdminParcelDetail} />
          )}
        </div>
      </div>
    </div>
  );
}
